/**
 * @file   src/helpers/matchDemandUtils.js
 * @brief  Tier detection, insight text generation, and demand analysis for Match tab
 */
import { formatCompactPrice, computeSweetSpotRange } from "./priceCapUtils";
import { BUY, LONG_RENT } from "../constants/common";

/**
 * Detect which tier of the demand display to show based on match count.
 *
 * Tier 1: 3+ matches - full experience (stats + histogram + slider + insight)
 * Tier 2: 1-2 matches - slider + insight (no stats, no histogram)
 * Tier 3: 0 matches - notification promise (LowDataState)
 *
 * @param {number} matchCount - Total number of matches
 * @returns {1|2|3}
 */
export const detectTier = (matchCount) => {
  if (matchCount >= 3) return 1;
  if (matchCount >= 1) return 2;
  return 3;
};

/**
 * Get the price label for the ad type (purchase price vs rent vs nightly).
 */
const getPriceLabel = (adType) => {
  if (adType === BUY) return "price range";
  if (adType === LONG_RENT) return "rent range";
  return "nightly rate range";
};

/**
 * Build contextual insight text based on asking price, slider position, and demand data.
 *
 * @param {number} askingPrice - The property's asking price
 * @param {number} sliderPrice - Current slider position (may differ from asking)
 * @param {number[]} prices - Array of match budget prices
 * @param {number} matchCount - Total match count
 * @param {string} adType - BUY, LONG_RENT, or SHORT_RENT
 * @param {Object[]} adList - Full ad objects (for range overlap counting)
 * @returns {string} Human-readable insight text
 */
export const buildInsightText = (askingPrice, sliderPrice, prices, matchCount, adType, adList) => {
  // Tier 3 - no matches
  if (!matchCount || matchCount === 0) {
    return "You'll be notified when buyers post Want-Listings that match your property.";
  }

  // Tier 2 - 1-2 matches (slider + insight)
  if (matchCount <= 2) {
    return `${matchCount} qualified buyer${matchCount > 1 ? "s" : ""} actively searching in your ${getPriceLabel(adType)}. New Want-Listings are posted daily as agents represent more buyers.`;
  }

  // Tier 1 - full experience with slider interaction
  if (!prices || prices.length === 0 || !askingPrice) {
    return `${matchCount} qualified buyers are actively searching for properties like yours. This is live demand data - updated as agents post new Want-Listings.`;
  }

  const sweetSpot = computeSweetSpotRange(prices, adType);

  // If slider is at asking price (or not moved), show general insight
  if (!sliderPrice || sliderPrice === askingPrice) {
    if (sweetSpot) {
      return `The highest concentration of buyer demand falls between ${formatCompactPrice(sweetSpot.min)} and ${formatCompactPrice(sweetSpot.max)}. This is where the most Want-Listings overlap with your property.`;
    }
    return `${matchCount} qualified buyers are actively searching in your ${getPriceLabel(adType)}. Adjust the slider to explore demand at different price points.`;
  }

  // Slider has been moved - show price-specific insight
  const diff = sliderPrice - askingPrice;
  const pctDiff = Math.abs(diff / askingPrice * 100).toFixed(0);

  // Count matches whose budget range overlaps the slider price (same logic as feed filter)
  const tolerance = adType === BUY ? 25000 : adType === LONG_RENT ? 250 : 50;
  const nearbyCount = adList && adList.length > 0
    ? adList.filter((ad) => {
        const mn = ad.Budget_mn || 0;
        const mx = ad.Budget_mx || mn;
        return sliderPrice >= (mn - tolerance) && sliderPrice <= (mx + tolerance);
      }).length
    : prices.filter((p) => Math.abs(p - sliderPrice) <= tolerance).length;

  if (diff > 0) {
    // Slider above asking price
    if (nearbyCount > 0) {
      return `${nearbyCount} buyer${nearbyCount > 1 ? "s" : ""} actively searching near ${formatCompactPrice(sliderPrice)} - that's ${pctDiff}% above your asking price. Strong demand at this level could support a higher list price.`;
    }
    return `Buyer activity thins out at ${formatCompactPrice(sliderPrice)} (${pctDiff}% above asking). Consider pricing closer to the demand sweet spot for faster movement.`;
  }

  // Slider below asking price
  if (nearbyCount > 0) {
    return `${nearbyCount} buyer${nearbyCount > 1 ? "s" : ""} actively searching near ${formatCompactPrice(sliderPrice)} - ${pctDiff}% below your asking price. This suggests room for a competitive pricing conversation.`;
  }
  return `Limited buyer activity at ${formatCompactPrice(sliderPrice)} (${pctDiff}% below asking). Most demand concentrates at higher price points.`;
};
